/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { createPortal } from 'react-dom';
import type { Personnel } from '../../../../shared/types/game';
import type { Competence } from '../../../../shared/types/config';
import OperationalPlanningModal, {
  PLANNING_GROUPE_KEYS,
  type PlanningGroupeKey,
} from '../OperationalPlanningModal';
import { statuts } from '../../../../config';
import PersonnelDetailsGeneralInfo from './PersonnelDetailsGeneralInfo';

interface PersonnelDetailsModalProps {
  showPersonnelDetailsModal: boolean;
  setShowPersonnelDetailsModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedPersonnel: Personnel | null;
  setSelectedPersonnel: React.Dispatch<React.SetStateAction<Personnel | null>>;
  personnelCompetences: Competence[];
  gradeDefaultCompetences: Competence[];
  loadingCompetences: boolean;
  competenceSearchQuery: string;
  setCompetenceSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  posteNom: string | null;
  statutGeneral: string | null;
  statutPlanning: string | null;
  derniereIntervention: { date: string; heure: string } | null;
  personnelGroupeSelect: '' | PlanningGroupeKey;
  savingGroupe: boolean;
  editingNom: boolean;
  setEditingNom: React.Dispatch<React.SetStateAction<boolean>>;
  nomValue: string;
  setNomValue: React.Dispatch<React.SetStateAction<string>>;
  prenomValue: string;
  setPrenomValue: React.Dispatch<React.SetStateAction<string>>;
  savingNom: boolean;
  deletingPersonnelId: number | null;
  buildingType: string;
  buildingName?: string;
  showOperationalPlanningModal: boolean;
  setShowOperationalPlanningModal: React.Dispatch<React.SetStateAction<boolean>>;
  buildingId: number;
  handleSaveNom: () => Promise<void>;
  handleCancelEditNom: () => void;
  handleDeletePersonnel: (p: Personnel) => Promise<void>;
  savePersonnelGroupe: (v: '' | PlanningGroupeKey) => Promise<void>;
  getStatutGeneralColors: (s: string | null) => any;
  getStatutPlanningColors: (s: string | null) => any;
  t: (key: string) => string;
}

const PersonnelDetailsModal: React.FC<PersonnelDetailsModalProps> = (props) => {
  const {
    showPersonnelDetailsModal,
    setShowPersonnelDetailsModal,
    selectedPersonnel,
    setSelectedPersonnel,
    personnelCompetences,
    gradeDefaultCompetences,
    loadingCompetences,
    competenceSearchQuery,
    setCompetenceSearchQuery,
    posteNom,
    statutGeneral,
    statutPlanning,
    derniereIntervention,
    personnelGroupeSelect,
    savingGroupe,
    editingNom,
    setEditingNom,
    nomValue,
    setNomValue,
    prenomValue,
    setPrenomValue,
    savingNom,
    deletingPersonnelId,
    buildingType,
    buildingName,
    showOperationalPlanningModal,
    setShowOperationalPlanningModal,
    buildingId,
    handleSaveNom,
    handleCancelEditNom,
    handleDeletePersonnel,
    savePersonnelGroupe,
    getStatutGeneralColors,
    getStatutPlanningColors,
    t,
  } = props;

  if (!showPersonnelDetailsModal || !selectedPersonnel) return null;
  return createPortal(
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4" 
          onClick={() => {
            setShowPersonnelDetailsModal(false);
            setSelectedPersonnel(null);
          }}
        >
          <div 
            className="bg-gray-900 rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden border border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* En-tête */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gray-800/50">
              <div className="flex items-center gap-4">
                {selectedPersonnel.grade_img ? (
                  <img 
                    src={selectedPersonnel.grade_img} 
                    alt={selectedPersonnel.grade || 'Grade'} 
                    className="h-12 w-12 object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="h-12 w-12 bg-red-600 rounded flex items-center justify-center text-white font-bold">
                    ?
                  </div>
                )}
                <div className="flex-1">
                  {editingNom ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={prenomValue}
                          onChange={(e) => setPrenomValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveNom();
                            } else if (e.key === 'Escape') {
                              handleCancelEditNom();
                            }
                          }}
                          className="text-2xl font-bold text-white bg-gray-700 border border-blue-500 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Prénom"
                          autoFocus
                          disabled={savingNom}
                        />
                        <input
                          type="text"
                          value={nomValue}
                          onChange={(e) => setNomValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveNom();
                            } else if (e.key === 'Escape') {
                              handleCancelEditNom();
                            }
                          }}
                          className="text-2xl font-bold text-white bg-gray-700 border border-blue-500 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Nom"
                          disabled={savingNom}
                        />
                      </div>
                      <button
                        onClick={handleSaveNom}
                        disabled={savingNom || !nomValue.trim() || !prenomValue.trim()}
                        className="text-green-400 hover:text-green-300 transition-colors p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Sauvegarder"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                      <button
                        onClick={handleCancelEditNom}
                        disabled={savingNom}
                        className="text-red-400 hover:text-red-300 transition-colors p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Annuler"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <h2 className="text-2xl font-bold text-white">
                        {selectedPersonnel.prenom} {selectedPersonnel.nom}
                      </h2>
                      <button
                        onClick={() => setEditingNom(true)}
                        className="text-gray-400 hover:text-white transition-colors p-1"
                        title="Modifier le nom"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeletePersonnel(selectedPersonnel)}
                        disabled={deletingPersonnelId === selectedPersonnel.id}
                        className="text-red-400 hover:text-red-300 transition-colors p-1 disabled:opacity-50"
                        title="Supprimer ce personnel"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )}
                  <p className="text-blue-400 text-sm mt-1">{selectedPersonnel.grade}</p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setShowPersonnelDetailsModal(false);
                  setSelectedPersonnel(null);
                }}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Contenu */}
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-120px)]">
              <PersonnelDetailsGeneralInfo
                selectedPersonnel={selectedPersonnel}
                statutGeneral={statutGeneral}
                statutPlanning={statutPlanning}
                derniereIntervention={derniereIntervention}
                posteNom={posteNom}
                personnelGroupeSelect={personnelGroupeSelect}
                savingGroupe={savingGroupe}
                savePersonnelGroupe={savePersonnelGroupe}
                getStatutGeneralColors={getStatutGeneralColors}
                getStatutPlanningColors={getStatutPlanningColors}
                t={t}
              />

              {/* Bonus */}
              {selectedPersonnel.bonus && selectedPersonnel.bonus.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Bonus ({selectedPersonnel.bonus.length})</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedPersonnel.bonus.map((bonus, idx) => (
                      <div key={idx} className="bg-gray-800 rounded-lg p-4 border border-purple-500/20">
                        <div className="flex items-center justify-between">
                          <span className="text-purple-400 font-medium">{bonus.type}</span>
                          <span className="text-white font-bold text-lg">+{bonus.valeur}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Compétences */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">
                    Compétences
                    {loadingCompetences && <span className="text-sm text-gray-400 ml-2">(Chargement...)</span>}
                  </h3>
                </div>
                {loadingCompetences ? (
                  <div className="text-center py-8 text-gray-400">
                    Chargement des compétences...
                  </div>
                ) : (() => {
                  // Combiner toutes les compétences (personnel + grade) et éviter les doublons
                  const allCompetencesMap = new Map<number, Competence>();
                  
                  // Ajouter les compétences du grade par défaut
                  gradeDefaultCompetences.forEach(comp => {
                    if (comp.id) {
                      allCompetencesMap.set(comp.id, comp);
                    }
                  });
                  
                  // Ajouter les compétences directes du personnel (prioritaires)
                  personnelCompetences.forEach(comp => {
                    if (comp.id) {
                      allCompetencesMap.set(comp.id, comp);
                    }
                  });
                  
                  let allCompetences = Array.from(allCompetencesMap.values());
                  
                  // Filtrer par recherche
                  if (competenceSearchQuery.trim()) {
                    const searchLower = competenceSearchQuery.toLowerCase().trim();
                    allCompetences = allCompetences.filter(c =>
                      c.nom.toLowerCase().includes(searchLower) ||
                      (c.description && c.description.toLowerCase().includes(searchLower))
                    );
                  }
                  
                  const competencesNormales = allCompetences.filter(c => !c.is_specialite);
                  const specialites = allCompetences.filter(c => c.is_specialite);

                  if (allCompetences.length === 0 && !competenceSearchQuery.trim()) {
                    return (
                      <div className="bg-gray-800 rounded-lg p-6 text-center text-gray-400">
                        <p>Aucune compétence enregistrée pour ce personnel</p>
                      </div>
                    );
                  }

                  if (allCompetences.length === 0 && competenceSearchQuery.trim()) {
                    return (
                      <div className="bg-gray-800 rounded-lg p-6 text-center text-gray-400">
                        <p>Aucune compétence trouvée pour "{competenceSearchQuery}"</p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-4">
                      {/* Barre de recherche pour les compétences */}
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Rechercher une compétence..."
                          value={competenceSearchQuery}
                          onChange={(e) => setCompetenceSearchQuery(e.target.value)}
                          className="w-full px-4 py-2 pl-10 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                        <svg
                          className="absolute left-3 top-2.5 w-4 h-4 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </div>

                      {/* Compétences normales */}
                      {competencesNormales.length > 0 && (
                        <div>
                          <h4 className="text-md font-semibold text-blue-400 mb-3 flex items-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Compétences ({competencesNormales.length})
                          </h4>
                          <div className="max-h-[300px] overflow-y-auto pr-2">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {competencesNormales.map((competence) => (
                                <div 
                                  key={competence.id} 
                                  className="bg-gray-800 rounded-lg p-2.5 border border-blue-500/20 hover:border-blue-500/40 transition-colors"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                      <span className="font-medium text-blue-400 block text-sm truncate" title={competence.nom}>
                                        {competence.nom}
                                      </span>
                                      {competence.description && (
                                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{competence.description}</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Spécialités */}
                      {specialites.length > 0 && (
                        <div>
                          <h4 className="text-md font-semibold text-yellow-400 mb-3 flex items-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                            </svg>
                            Spécialités ({specialites.length})
                          </h4>
                          <div className="max-h-[300px] overflow-y-auto pr-2">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {specialites.map((competence) => (
                                <div 
                                  key={competence.id} 
                                  className="bg-gray-800 rounded-lg p-2.5 border border-yellow-500/30 bg-yellow-500/5 hover:border-yellow-500/50 hover:bg-yellow-500/10 transition-colors"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="font-medium text-yellow-400 text-sm truncate" title={competence.nom}>
                                          {competence.nom}
                                        </span>
                                        <span className="text-xs bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded flex-shrink-0">
                                          Spé
                                        </span>
                                      </div>
                                      {competence.description && (
                                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{competence.description}</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Informations supplémentaires */}
              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-lg font-semibold text-white mb-4">Informations système</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400">ID Personnel</p>
                    <p className="text-white">{selectedPersonnel.id}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">ID Bâtiment</p>
                    <p className="text-white">{selectedPersonnel.batiment_id || 'Non assigné'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Date de création</p>
                    <p className="text-white">
                      {selectedPersonnel.created_at ? new Date(selectedPersonnel.created_at).toLocaleDateString('fr-FR') : 'N/A'}
                    </p>
                  </div>
                  {selectedPersonnel.updated_at && (
                    <div>
                      <p className="text-gray-400">Dernière mise à jour</p>
                      <p className="text-white">
                        {new Date(selectedPersonnel.updated_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
  );
};

export default PersonnelDetailsModal;
